import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Components
import Typography from '../../../../../components/Typography';
import AudioPlayer from '../../../../../components/AudioPlayer';

// Styles
import styles from './style.module.scss';

// Assets
import speaker1 from './assets/alex.png';
import speaker2 from './assets/mary.png';
import speaker3 from './assets/david.png';
import speaker4 from './assets/olivia.png';
import speaker5 from './assets/sam.png';
import speaker6 from './assets/emma.png';

// Assets
import audioSample from './assets/sounds/piano.wav';
import audioSample2 from './assets/sounds/alien.mp3';

// Types
type SpeakerType = 'analytical' | 'friendly' | 'emotional' | 'professional' | 'documentary' | 'narrative';

const SPEAKERS_OPTIONS: SpeakerType[] = ['analytical', 'friendly', 'emotional', 'professional', 'documentary', 'narrative'];

interface SpeakerOptionProps {
  speaker: SpeakerType;
  onSelect: (speaker: SpeakerType) => void;
  selected: boolean;
  index?: number;
}

const SpeakerOption: React.FC<SpeakerOptionProps> = ({ speaker, onSelect, selected, index = -1 }) => {
  const { t } = useTranslation();

  const Name = useCallback(() => {
    switch (speaker) {
      case 'analytical': return t('generate.podcast.speakers.alex');
      case 'friendly': return t('generate.podcast.speakers.mary');
      case 'emotional': return t('generate.podcast.speakers.david');
      case 'professional': return t('generate.podcast.speakers.olivia');
      case 'documentary': return t('generate.podcast.speakers.sam');
      case 'narrative': return t('generate.podcast.speakers.emma');
    }
  }, [speaker, t]);

  const Description = useCallback(() => {
    switch (speaker) {
      case 'analytical': return t('generate.podcast.speakers.alex_description');
      case 'friendly': return t('generate.podcast.speakers.mary_description');
      case 'emotional': return t('generate.podcast.speakers.david_description');
      case 'professional': return t('generate.podcast.speakers.olivia_description');
      case 'documentary': return t('generate.podcast.speakers.sam_description');
      case 'narrative': return t('generate.podcast.speakers.emma_description');
    }
  }, [speaker, t]);

  const Avatar = useCallback(() => {
    switch (speaker) {
      case 'analytical': return speaker1;
      case 'friendly': return speaker2;
      case 'emotional': return speaker3;
      case 'professional': return speaker4;
      case 'documentary': return speaker5;
      case 'narrative': return speaker6;
    }
  }, [speaker]);

  const Audio = useCallback(() => {
    switch (speaker) {
      case 'analytical': return audioSample;
      case 'friendly': return audioSample;
      case 'emotional': return audioSample;
      case 'professional': return audioSample2;
      case 'documentary': return audioSample;
      case 'narrative': return audioSample;
    }
  }, [speaker]);

  return (
    <div className={`${styles.speaker_option} ${selected ? styles.selected : ''}`}>
      <div
        className={styles.speaker_option_info}
        onClick={() => onSelect(speaker)}
      >
        <div className={styles.speaker_option_avatar}>
          <img src={Avatar()} alt={Name()} />
        </div>

        <div className={styles.speaker_option_name}>
          <Typography variant='p'>
            {Name()}
          </Typography>

          <Typography variant='p'>
            {Description()}
          </Typography>
        </div>

        {index !== -1 && (
          <div className={styles.speaker_option_index}>
            <Typography variant='p'>
              {index + 1}
            </Typography>
          </div>
        )}
      </div>

      <div className={styles.speaker_option_actions}>
        <AudioPlayer audioUrl={Audio()} withWaveform />
      </div>
    </div>
  )
}

interface SpeakersSelectProps {
  onChange?: (speakers: SpeakerType[]) => void;
}

const SpeakersSelect: React.FC<SpeakersSelectProps> = ({ onChange }) => {
  // Hooks
  const { t } = useTranslation();

  // States
  const [selectedSpeakers, setSelectedSpeakers] = useState<SpeakerType[]>([]);

  // Memoized Values

  // Methods
  const handleSpeakerSelect = useCallback((speaker: SpeakerType) => {
    setSelectedSpeakers((prev) => {
      if (prev.includes(speaker)) {
        return prev.filter((s) => s !== speaker);
      }

      const newSelectedSpeakers = [...prev, speaker];
      onChange?.(newSelectedSpeakers);
      return newSelectedSpeakers;
    });
  }, []);

  // States
  return (
    <div className={styles.root}>
      {SPEAKERS_OPTIONS.map((speaker) => (
        <SpeakerOption
          key={speaker}
          speaker={speaker}
          onSelect={handleSpeakerSelect}
          selected={selectedSpeakers.includes(speaker)}
          index={selectedSpeakers.indexOf(speaker)}
        />
      ))}
    </div>
  )
};

export default SpeakersSelect;
